import {
	Box,
	CircularProgress,
	Divider,
	Stack,
	Tab,
	Tabs,
	Typography,
	styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import RequestedContacts from "./RequestedContacts";
import IgnoredContacts from "./IgnoredContacts";
import SelectedContactDetails from "./SelectedContactDetails";
import { useDispatch, useSelector } from "react-redux";
import {
	clearAcceptIgnoreContact,
	fetchContacts,
	removeContactStatus,
	removeSelectedContact,
	selectedContactAction,
	syncContactsWithCache,
	updateIgnoreContacts,
	updatePendingContacts,
	updateSelectedPendingContact,
} from "../../../state/actions/contacts";
import store from "../../../state/configureStore";
import {
	LFStorageKeys,
	getDataFromLFStorage,
	saveDataInLFStorage,
} from "../../../services/storage/LFStorage";
import {
	acceptContacts,
	ignoreContacts,
} from "../../../services/clients/ContactsService";
import {
	clearAcceptIgnoreButtons,
} from "../../../state/actions/chats";
import { showToast } from "../../../components/ui/toaster";

const StyledTabLabel = styled(Typography)({
	fontSize: "16px",
	fontFamily: "Figtree-Regular",
	textTransform: "capitalize",
	fontWeight: 600,
});

const StyledHeading = styled(Typography)({
	fontSize: "22px",
	fontWeight: "700",
	fontFamily: "Figtree-Regular",
	color: "#404585",
	textAlign: "center",
});

const OverlayLoaderContainer = styled(Stack)({
	backgroundColor: "rgba(0,0,0,0.2)",
	height: "100%",
	width: "100%",
	zIndex: "5",
	position: "absolute",
	pointerEvents: 'not-allowed',
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
});

const CustomTabPanel = (props) => {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ pt: 2 }}>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
};

function PendingRequestPopup(props) {
	const [selectedTab, setSelectedTab] = useState(0);
	const {
		selectedPendingContact,
		pendingContacts,
		ignored: ignoredContacts,
	} = useSelector((state) => state.contacts);
	const selectedDomain = useSelector((state) => state.domain.selectedDomainDetails);
	const [showOverlayLoader, setShowOverlayLoader] = useState(false);
	const userDomain = selectedDomain?.userDomain || "";
	const dispatch = useDispatch();

	// Change the selected tab
	const handleTabChange = (e, newValue) => {
		setSelectedTab(newValue);
		dispatch(updateSelectedPendingContact({}));
	};

	const cacheContactAction = async (user, action) => {
		let contactsList =
			(await getDataFromLFStorage(
				`${LFStorageKeys.CONTACTS}_${userDomain}`
			)) || [];
  console.log('contactsList',contactsList)
		if (contactsList) {
			if (action === "accept") {
				contactsList?.contacts?.forEach((contact) => {
					if (contact.userId === user.userId) {
						contact.showAcceptIgnoreMsg = false;
						store.dispatch(selectedContactAction(contact));
					}
				});
			} else if (action === "ignore") {
				contactsList.contacts = contactsList?.contacts?.filter(
					(contact) => contact.userId !== user.userId
				);
				contactsList?.ignored?.push({
					userId: user.userId,
					userName: user.userName,
				});
				store.dispatch(removeSelectedContact());
			}

			saveDataInLFStorage(
				`${LFStorageKeys.CONTACTS}_${userDomain}`,
				contactsList
			);
			store.dispatch(syncContactsWithCache(contactsList));
		}
	};

	const accept = (selectedContact) => {
		setShowOverlayLoader(true);
		let updatedPendingContacts = pendingContacts.filter(
			(item) => item?.userId !== selectedContact?.userId
		);
		let updatedIgnoredContacts = ignoredContacts.filter(
			(item) => item?.userId !== selectedContact?.userId
		);
		cacheContactAction(selectedContact, "accept");
		acceptContacts([selectedContact?.userId], userDomain)
			.then(() => {
				store.dispatch(clearAcceptIgnoreButtons());
				store.dispatch(clearAcceptIgnoreContact());
				store.dispatch(updatePendingContacts(updatedPendingContacts));
				store.dispatch(updateIgnoreContacts(updatedIgnoredContacts));
				store.dispatch(
					fetchContacts({ selectedDomain: userDomain }, true)
				);
				if (selectedContact) store.dispatch(updateSelectedPendingContact({}));
				setShowOverlayLoader(false);
				showToast('success', `Contact ${selectedContact.userName} is accepted`);
			})
			.catch((error) => {
				setShowOverlayLoader(false);
				showToast('error', `Error while accepting the contact ${selectedContact.userName}. Please try again later.`);

			});
	};

	const ignore = (selectedContact) => {
		setShowOverlayLoader(true);
		cacheContactAction(selectedContact, "ignore");
		store.dispatch(removeContactStatus(false));
		let updatedPendingContacts = pendingContacts.filter(
			(item) => item?.userId !== selectedContact?.userId
		);
		let updatedIgnoredContacts = [...ignoredContacts, selectedContact];
		ignoreContacts([selectedContact?.userId], userDomain)
			.then(() => {
				store.dispatch(removeContactStatus(true));
				store.dispatch(clearAcceptIgnoreButtons());
				store.dispatch(clearAcceptIgnoreContact());
				store.dispatch(updatePendingContacts(updatedPendingContacts));
				store.dispatch(updateIgnoreContacts(updatedIgnoredContacts));
				if (selectedContact) store.dispatch(updateSelectedPendingContact({}));
				setShowOverlayLoader(false);
				showToast('success', `Contact ${selectedContact.userName} is ignored`);
			})
			.catch((error) => {
				setShowOverlayLoader(false);
				showToast('error', `Error while ignoring the contact ${selectedContact.userName}. Please try again later.`);
			});
	};

	useEffect(() => {
		return () => {
			dispatch(updateSelectedPendingContact({}));
		};
	}, []);

	return (
		<Box>
			{showOverlayLoader && (
				<OverlayLoaderContainer
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
				>
					<CircularProgress />
				</OverlayLoaderContainer>
			)}
			<StyledHeading className="!text-[18px]">Awaiting Attention</StyledHeading>
			<Stack
				flexDirection="row"
				sx={{ width: "100%", minHeight: "23rem", mt: 2 }}
			>
				<Box sx={{ width: "50%" }}>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							TabIndicatorProps={{
								style: {
									backgroundColor: "#6267AD",
									height: "3px",
									borderRadius: "4px",
								},
							}}
							value={selectedTab}
							textColor="#6267AD"
							onChange={handleTabChange}
							variant="fullWidth"
						>
							<Tab
								label={
									<StyledTabLabel
										sx={{ color: selectedTab === 0 ? "#6267AD" : "#8387BD" }}
									>
										Requested
									</StyledTabLabel>
								}
							/>
							<Tab
								label={
									<StyledTabLabel
										sx={{ color: selectedTab === 1 ? "#6267AD" : "#8387BD" }}
									>
										Ignored
									</StyledTabLabel>
								}
							/>
						</Tabs>
					</Box>
					<CustomTabPanel value={selectedTab} index={0}>
						<RequestedContacts
							ignore={ignore}
							accept={accept}
							allProfileImages={props.allProfileImages}
						/>
					</CustomTabPanel>
					<CustomTabPanel value={selectedTab} index={1}>
						<IgnoredContacts
							ignore={ignore}
							accept={accept}
							allProfileImages={props.allProfileImages}
						/>
					</CustomTabPanel>
				</Box>
				<Divider orientation="vertical" flexItem sx={{ margin: "0 12px" }} />
				<SelectedContactDetails
					allProfileImages={props.allProfileImages}
					ignore={ignore}
					accept={accept}
					showOverlayLoader={showOverlayLoader}
				/>
			</Stack>
		</Box>
	);
}

export default PendingRequestPopup;
